import {
	collection,
	doc,
	getDoc,
	getDocs,
	updateDoc,
	deleteDoc,
	Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { Agent, AgentInsight } from '../types/agent';

export class AgentService {
	// Agent Operations
	static async getAgent(agentId: string): Promise<Agent | null> {
		try {
			const agentDoc = await getDoc(doc(db, 'agents', agentId));
			if (agentDoc.exists()) {
				const data = agentDoc.data();
				return {
					id: agentDoc.id,
					...data,
					createdAt: data.createdAt?.toDate?.() || new Date(),
					updatedAt: data.updatedAt?.toDate?.() || new Date(),
					lastLoginAt: data.lastLoginAt?.toDate?.()
				} as Agent;
			}
			return null;
		} catch (error) {
			throw error;
		}
	}

	static async updateAgent(agentId: string, updates: Partial<Agent>): Promise<void> {
		try {
			const agentRef = doc(db, 'agents', agentId);
			await updateDoc(agentRef, {
				...updates,
				updatedAt: Timestamp.now()
			});
		} catch (error) {
			throw error;
		}
	}

	static async getAllAgents(): Promise<Agent[]> {
		try {
			// Get all agents without ordering to avoid filtering out agents without createdAt
			const agentsSnapshot = await getDocs(collection(db, 'agents'));
			
			const agents = agentsSnapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data(),
				createdAt: doc.data().createdAt?.toDate?.() || new Date(),
				updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
				lastLoginAt: doc.data().lastLoginAt?.toDate?.()
			})) as Agent[];

			// Sort in memory by createdAt (most recent first)
			return agents.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
		} catch (error) {
			console.error('Error getting all agents:', error);
			throw error;
		}
	}



	static async updateInsightStatus(
		insightId: string,
		status: AgentInsight['status'],
		resolvedBy?: string
	): Promise<void> {
		try {
			const insightRef = doc(db, 'agentInsights', insightId);
			const updates: any = { status };

			if (status === 'resolved' && resolvedBy) {
				updates.resolvedAt = Timestamp.now();
				updates.resolvedBy = resolvedBy;
			}

			await updateDoc(insightRef, updates);
		} catch (error) {
			throw error;
		}
	}


	// Search Operations

	// Create agent with invitation
	static async createAgentWithInvitation(agentData: {
		email: string;
		firstName: string;
		lastName: string;
		phone: string;
		companyName: string;
		role: 'agent' | 'master_agent' | 'super_agent';
		territory?: string;
		licenseNumber?: string;
		idempotencyKey?: string;
	}): Promise<{
		agentId: string;
		emailSent: boolean;
		emailError?: string | null;
		message: string;
	}> {
		// Derived from the email, not from Date.now() + Math.random(). A key that
		// is fresh on every call is not an idempotency key: double-clicking
		// Submit produced two different keys, and so two agents and two
		// invitation emails. Creating the same agent twice inside the backend's
		// 24h window is exactly the duplicate this is meant to collapse.
		const dataWithKey = {
			...agentData,
			idempotencyKey:
				agentData.idempotencyKey || `create-agent-${agentData.email.trim().toLowerCase()}`
		};

		const createFunction = httpsCallable(functions, 'createAgentWithInvitation');
		const result = await createFunction(dataWithKey);
		const data = result.data as {
			success: boolean;
			agentId: string;
			invitationId: string;
			emailSent: boolean;
			emailError?: string | null;
			existingUser: boolean;
			message: string;
		};

		if (!data.success) {
			throw new Error(data.message || 'Failed to create agent');
		}

		return {
			agentId: data.agentId,
			emailSent: data.emailSent,
			emailError: data.emailError,
			message: data.message
		};
	}

	// Delete agent
	static async deleteAgent(agentId: string): Promise<void> {
		try {
			// Delete the agent document
			await deleteDoc(doc(db, 'agents', agentId));

			// TODO: Also delete associated user authentication account if exists
			// This would typically be done through a Firebase Function

		} catch (error) {
			throw error;
		}
	}

	/**
	 * Checks an invitation link.
	 *
	 * This used to read the agentInvitations collection straight from the
	 * browser - which required that collection to be world-readable, and it
	 * held a working temporary password. Both are now server-side only, and the
	 * token in the link is what gets verified.
	 */
	static async validateInvitation(email: string, token: string): Promise<{
		firstName: string;
		email: string;
		role: string;
	}> {
		const validate = httpsCallable<
			{ email: string; token: string },
			{ valid: boolean; firstName: string; email: string; role: string }
		>(functions, 'validateAgentInvitation');

		const result = await validate({ email, token });
		return {
			firstName: result.data.firstName,
			email: result.data.email,
			role: result.data.role
		};
	}

	/**
	 * Redeems the invitation. The server sets the password and returns a custom
	 * token, so no server-generated credential ever reaches this client.
	 */
	static async completeAgentRegistration(
		email: string,
		token: string,
		password: string
	): Promise<{ agentId: string; customToken: string }> {
		const completeRegistration = httpsCallable<
			{ email: string; token: string; password: string },
			{ success: boolean; agentId: string; customToken: string }
		>(functions, 'completeAgentRegistration');

		const result = await completeRegistration({ email, token, password });

		if (!result.data.success) {
			throw new Error('Failed to complete registration');
		}

		return { agentId: result.data.agentId, customToken: result.data.customToken };
	}

	// Resend agent invitation
	static async resendInvitation(agentId: string): Promise<{
		success: boolean;
		emailSent: boolean;
		emailError?: string | null;
		message: string;
	}> {
		try {
			const resendFunction = httpsCallable(functions, 'resendAgentInvitation');
			const result = await resendFunction({ agentId });
			const data = result.data as {
				success: boolean;
				emailSent: boolean;
				emailError?: string | null;
				message: string;
			};
			return data;
		} catch (error) {
			throw error;
		}
	}

	static async setAgentStatus(agentId: string, isActive: boolean): Promise<void> {
		try {
			const setStatusFunction = httpsCallable(functions, 'setAgentStatus');
			await setStatusFunction({ agentId, isActive });
		} catch (error) {
			throw error;
		}
	}
} 